import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle, Edit, Trash2, Award, Eye, EyeOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { AwardMembership } from '@/types/awardMembership';
import AwardMembershipForm from './AwardMembershipForm';
import { supabase } from '@/integrations/supabase/client';

const AwardMemberships: React.FC = () => {
  const [memberships, setMemberships] = useState<AwardMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editMembership, setEditMembership] = useState<AwardMembership | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [membershipToDelete, setMembershipToDelete] = useState<string | null>(null);

  const fetchMemberships = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('award_memberships')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setMemberships(data as unknown as AwardMembership[]);
    } catch (error) {
      console.error('Error fetching award memberships:', error);
      toast.error('Failed to load award memberships');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberships();
  }, []);

  const handleDelete = async () => {
    if (!membershipToDelete) return;
    
    try {
      const { error } = await supabase
        .from('award_memberships')
        .delete()
        .eq('id', membershipToDelete);
        
      if (error) throw error;
      setMemberships(memberships.filter(m => m.id !== membershipToDelete));
      toast.success('Award membership deleted successfully');
    } catch (error) {
      console.error('Error deleting award membership:', error);
      toast.error('Failed to delete award membership');
    } finally {
      setDeleteDialogOpen(false);
      setMembershipToDelete(null);
    }
  };

  const confirmDelete = (id: string) => {
    setMembershipToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleSuccess = () => {
    fetchMemberships();
    setShowAddForm(false);
    setEditMembership(null);
  };

  const renderMembershipCard = (membership: AwardMembership) => (
    <Card key={membership.id} className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{membership.organization}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setEditMembership(membership)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => confirmDelete(membership.id)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
        {membership.years_of_membership && (
          <CardDescription>{membership.years_of_membership} years of membership</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {membership.voting_categories?.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Voting Categories:</p>
              <div className="flex flex-wrap gap-2">
                {membership.voting_categories.map(category => (
                  <Badge key={category} variant="secondary">
                    {category}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm">
            <span>Discovery status:</span>
            {membership.allow_campaign_contact ? (
              <Badge variant="outline" className="flex items-center gap-1 text-green-500">
                <Eye className="h-3 w-3" />
                <span>Discoverable</span>
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1 text-muted-foreground">
                <EyeOff className="h-3 w-3" />
                <span>Private</span>
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Award Memberships</h3>
        <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
          <DialogTrigger asChild>
            <Button variant="default" size="sm" className="gap-2">
              <PlusCircle className="h-4 w-4" />
              <span>Add Membership</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <AwardMembershipForm onSuccess={handleSuccess} />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="py-8 text-center">
          <p className="text-muted-foreground">Loading memberships...</p>
        </div>
      ) : memberships.length === 0 ? (
        <Card className="bg-muted/50">
          <CardContent className="py-8 text-center">
            <Award className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No award memberships added</p>
            <p className="text-muted-foreground mb-4">
              Add your award voting memberships to be discovered by artists
            </p>
            <Button 
              onClick={() => setShowAddForm(true)}
              className="gap-2"
            >
              <PlusCircle className="h-4 w-4" />
              Add Membership
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div>
          {memberships.map(renderMembershipCard)}
        </div>
      )}

      <Dialog open={!!editMembership} onOpenChange={(open) => !open && setEditMembership(null)}>
        <DialogContent className="max-w-lg">
          {editMembership && (
            <AwardMembershipForm 
              membership={editMembership} 
              onSuccess={handleSuccess} 
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your award membership information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AwardMemberships;
